import { Fragment, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShopError, api, type CouponEffect, type DeliveryOptions, type LoyaltyPreview, type PickupPoint, type PromotionsPreview, type VoucherEffect, type WidgetPoint } from '../api';
import { PickupPointPicker } from '../components/PickupPointPicker';
import { PplWidgetPicker } from '../components/PplWidgetPicker';
import { formatCzk } from '../money';
import { useMoney } from '../currency';
import { useT } from '../i18n';
import { useCart } from '../cart';
import { useCustomer } from '../auth/CustomerContext';
import { rememberOrder } from '../orderMemory';
import { usePageMeta } from '../seo';
import { flush, getTrackingContext, track } from '../tracking/tracker';

/**
 * Počet bodů z políčka. Body jsou MĚNA a jsou nedělitelné, takže se z políčka
 * bere jen celé nezáporné číslo; strop je `maxPoints` ze serveru — nabídnout
 * víc by znamenalo, že pokladna uplatní míň, součet přestane sedět s účtovanou
 * částkou a objednávka spadne na stropu `expectedTotalMinor`.
 */
function clampPoints(raw: string, maxPoints: number): number {
  const parsed = Math.floor(Number(raw));
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.min(parsed, maxPoints);
}

export function CheckoutPage() {
  usePageMeta({ title: 'Pokladna | Super Krmivo', index: false });
  const navigate = useNavigate();
  const { t } = useT();
  const { customer } = useCustomer();
  const { format, isConverted, currency } = useMoney();
  const { lines, count, estimatedTotalMinor, clear } = useCart();

  const [options, setOptions] = useState<DeliveryOptions | null>(null);
  const [promo, setPromo] = useState<PromotionsPreview | null>(null);
  const [shippingMethod, setShippingMethod] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [pickupPoint, setPickupPoint] = useState<PickupPoint | null>(null);
  // Bod z widgetu dopravce – jina cesta, jiny tvar dat.
  const [widgetPoint, setWidgetPoint] = useState<WidgetPoint | null>(null);

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [countryCode, setCountryCode] = useState('CZ');
  const [note, setNote] = useState('');
  const [terms, setTerms] = useState(false);

  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState<CouponEffect | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucher, setVoucher] = useState<VoucherEffect | null>(null);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const [voucherBusy, setVoucherBusy] = useState(false);

  /**
   * VĚRNOSTNÍ BODY (#40).
   *
   * `loyalty` je náhled ze serveru: zůstatek, kurz, strop pro TENHLE košík
   * a zbytek základu po kupónu. Výloha z něj nic nedopočítává kromě jediného
   * násobení celým kurzem — vzorec (ořez základem, zaokrouhlení dolů na celý
   * bod, pořadí kupón → body → poukaz) žije na serveru v `splitCartDiscounts`
   * a náhled ho už použil.
   *
   * `pointsWanted` je přání zákazníka, vždy už oříznuté stropem. `null`
   * v `loyalty` znamená „body se nenabízejí" — host, nenačtený náhled i jeho
   * výpadek. Je to BEZPEČNÝ směr: bez bodů je souhrn nejvýš dražší, než co
   * pokladna účtuje, a levnější objednávka projde.
   */
  const [loyalty, setLoyalty] = useState<LoyaltyPreview | null>(null);
  const [pointsWanted, setPointsWanted] = useState(0);
  /** Session mezitím vypršela – body nabízet nesmíme, skončily by chybou. */
  const [pointsSignedOut, setPointsSignedOut] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const items = lines.map((line) => ({ variantId: line.variantId, quantity: line.quantity }));

  /**
   * KLIC NAPOSLED ODESLANEHO DOTAZU NA `/api/shop/voucher`.
   *
   * Obsahuje presne to, z ceho se sklada telo dotazu: kosik, kod poukazu
   * a kod kuponu. Diky nemu efekt nize pozna, ze uz tentyz dotaz poslalo
   * tlacitko, a neposle ho podruhe. `useRef` (ne stav) zamerne – zmena klice
   * nesmi vyvolat prekresleni.
   */
  const lastVoucherQuery = useRef<string | null>(null);
  const voucherQueryKey = (code: string, couponCode: string | null): string =>
    JSON.stringify([items, code, couponCode]);

  // CDP: checkout_started jednou per mount, az kdyz je v kosiku neco
  // (prazdna pokladna zobrazi jen hlasku a nikam nevede).
  const checkoutTracked = useRef(false);
  useEffect(() => {
    if (checkoutTracked.current || lines.length === 0) return;
    checkoutTracked.current = true;
    track('checkout_started', {
      item_count: count,
      estimated_total_minor: estimatedTotalMinor,
      items: lines.map((l) => ({
        variant_id: l.variantId,
        quantity: l.quantity,
        price_minor: l.priceMinor,
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines]);

  // Nabidka dopravy zavisi na obsahu kosiku i na uz zvolene doprave
  // (dobirka nemusi byt vsude).
  useEffect(() => {
    if (items.length === 0) return;
    api
      .deliveryOptions(items, shippingMethod === '' ? undefined : shippingMethod)
      .then((result) => {
        setOptions(result);
        setShippingMethod((current) =>
          current !== '' && result.shipping.some((offer) => offer.code === current)
            ? current
            : (result.shipping[0]?.code ?? ''),
        );
        setPaymentMethod((current) =>
          current !== '' && result.payment.some((option) => option.code === current)
            ? current
            : (result.payment[0]?.code ?? ''),
        );
      })
      .catch((caught: unknown) => {
        setError(caught instanceof ShopError ? caught.message : t('co.errShipping'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, shippingMethod]);

  // Nahled automatickych akci pro souhrn (rozpis + darky + usetreno).
  // Debounce + zruseni predchoziho volani, FAIL-SILENT: vypadek jen skryje
  // akci, pokladna funguje dal. Zavazne se vsechno stejne pocita az na serveru.
  useEffect(() => {
    if (items.length === 0) {
      setPromo(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .fetchPromotions(items)
        .then((result) => {
          if (!cancelled) setPromo(result);
        })
        .catch(() => {
          if (!cancelled) setPromo(null);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines]);

  /**
   * DRZI UPLATNENOU CASTKU POUKAZU AKTUALNI.
   *
   * Poukaz kryje jen zbozi po akcich a PO KUPONU, takze uplatnena castka se
   * meni s obsahem kosiku i s kuponem. Kdyby v souhrnu zustala stara, ukazal
   * by soucet jine cislo, nez pokladna uctuje - a protoze TENTYZ soucet
   * odchazi jako `expectedTotalMinor`, prestal by platit i strop.
   *
   * Kdyz se overeni nepodari, poukaz ze souhrnu radeji zmizi: souhrn BEZ
   * poukazu je nejvyse vyssi nez uctovana castka, a levnejsi objednavka
   * pokladnou projde. Naopak by tise zdrazila.
   */
  const appliedVoucherCode = voucher?.code ?? null;
  const appliedCouponCode = coupon?.code ?? null;
  useEffect(() => {
    if (appliedVoucherCode === null) return;
    // JEDEN DOTAZ NA JEDNU ZMENU, NE DVA.
    //
    // Efekt se rozjede i po tom, co poukaz uplatni tlacitko: `applyVoucher()`
    // (niz) si odpoved vyzada sam a jejim ulozenim zmeni `appliedVoucherCode`
    // z null na kod, cimz spusti tenhle efekt – a ten posilal DOSLOVA TYZ
    // dotaz podruhe. Zmereno na skutecne vyloze: jedno kliknuti na „Použít"
    // = dva POST /api/shop/voucher. Kazdy takovy dotaz ubira z brzdy na
    // hadani kodu (60 pozadavku / 600 s na IP), o kterou se za sdilenou
    // adresou (firemni NAT, mobilni CGNAT) deli vsichni zakaznici, takze si
    // navzajem vycerpaji rozpocet a zablokuji uplatneni poukazu i kuponu.
    //
    // `lastVoucherQuery` drzi klic naposled ODESLANEHO dotazu (kosik + kod
    // poukazu + kod kuponu). Kdyz efekt zjisti, ze by poslal presne tentyz
    // dotaz, neposle nic – odpoved uz je v `voucher`. Kazda skutecna zmena
    // (jiny kupon, jiny poukaz, jiny kosik) klic meni, takze se dotaz posle.
    const query = voucherQueryKey(appliedVoucherCode, appliedCouponCode);
    if (lastVoucherQuery.current === query) return;
    // DEBOUNCE 300 ms – stejne jako nahled akci vyse. Efekt se dnes spousti
    // z ulozeneho kuponu (uplatneni i odebrani) a jako pojistka i ze zmeny
    // kosiku; na TETO strance zadne ovladani mnozstvi neni (`setQuantity` je
    // jen na CartPage) a kosik se behem pokladny nemeni, takze zdrzeni tu
    // hlavne slouci rychly sled zmen kuponu do jednoho dotazu. Zruseni
    // predchoziho behu navic brani probliknuti odpovedi ze zastaraleho
    // pozadavku.
    let cancelled = false;
    const timer = setTimeout(() => {
      lastVoucherQuery.current = query;
      api
        .applyVoucher(items, appliedVoucherCode, appliedCouponCode ?? undefined)
        .then((result) => {
          if (!cancelled) setVoucher(result.voucher);
        })
        .catch((caught: unknown) => {
          if (cancelled) return;
          setVoucher(null);
          // Server umi rict, ze problem neni v poukazu, ale v KUPONU (uz nema
          // platnost, kosik klesl pod minimum). Pak musi zmizet i kupon –
          // jinak by souhrn dal odecital slevu, kterou pokladna neuzna.
          if (caught instanceof ShopError && caught.field === 'couponCode') {
            setCoupon(null);
            setCouponError(caught.message);
            setVoucherError(null);
            return;
          }
          setVoucherError(caught instanceof ShopError ? caught.message : t('co.errVoucher'));
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, appliedVoucherCode, appliedCouponCode]);

  /**
   * DRŽÍ STROP NA BODY AKTUÁLNÍ.
   *
   * Kolik bodů na košík sedne, závisí na obsahu košíku a na KUPÓNU (kupón se
   * uplatňuje před body). Kdyby v souhrnu zůstal starý strop, zaškrtl by
   * zákazník víc bodů, než pokladna uplatní — hotovost by pak vyšla dráž, než
   * co viděl, a objednávka by spadla na `expectedTotalMinor`.
   *
   * POČET BODŮ V ZÁVISLOSTECH ZÁMĚRNĚ NENÍ. Odpověď na něm nezávisí (viz
   * `LoyaltyPreview` v `../api`), takže hraní s políčkem nevyrábí další
   * dotazy — a kdyby ano, s kupónem v košíku by ubíraly z brzdy na hádání
   * kódů, o kterou se za sdílenou IP dělí všichni zákazníci.
   *
   * HOST SE NEPTÁ VŮBEC: endpoint je jen pro přihlášené a anonymovi vrátí 401.
   * Dokud `/me` nedoběhne (`customer === undefined`), taky ne — jinak by první
   * dotaz odešel bez cookie a zbytečně skončil 401.
   *
   * FAIL-SILENT: když se náhled nepodaří, body ze souhrnu zmizí (viz stav výš).
   * 401 navíc znamená, že session vypršela, a to zákazníkovi řekneme.
   */
  useEffect(() => {
    if (customer === undefined || customer === null) {
      setLoyalty(null);
      setPointsWanted(0);
      return;
    }
    let cancelled = false;
    // Debounce 300 ms – stejně jako náhled akcí a poukazu výš.
    const timer = setTimeout(() => {
      api
        .loyaltyPreview(items, appliedCouponCode ?? undefined)
        .then((result) => {
          if (cancelled) return;
          setLoyalty(result.loyalty);
          setPointsSignedOut(false);
          // Strop mohl klesnout (menší košík, nový kupón) – přání se mu musí
          // podřídit, jinak by souhrn ukazoval slevu, kterou pokladna neuzná.
          setPointsWanted((wanted) => Math.min(wanted, result.loyalty.maxPoints));
        })
        .catch((caught: unknown) => {
          if (cancelled) return;
          setLoyalty(null);
          setPointsWanted(0);
          if (caught instanceof ShopError && caught.status === 401) setPointsSignedOut(true);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, appliedCouponCode, customer]);

  if (lines.length === 0) {
    return (
      <>
        <h1>{t('co.title')}</h1>
        <p className="empty muted">{t('cart.emptyMsg')}</p>
        <Link className="button primary" to="/">{t('cart.backToShop')}</Link>
      </>
    );
  }

  const shipping = options?.shipping.find((offer) => offer.code === shippingMethod) ?? null;
  const payment = options?.payment.find((option) => option.code === paymentMethod) ?? null;
  const itemsTotal = options?.cart.itemsTotalMinor ?? 0;
  // Kupon na dopravu zdarma nuluje dopravu, ostatni snizuji zbozi.
  const shippingCost =
    coupon?.freeShipping === true ? 0 : (shipping?.priceMinor ?? 0);
  const discount = coupon?.discountMinor ?? 0;
  // POUKAZ PATRI DO SOUCTU. Do radku jde UPLATNENA castka (`appliedMinor`),
  // ne zustatek - poukaz kryje jen zbozi po akcich a po kuponu, ne dopravu
  // ani priplatek za platbu. Dokud tenhle clen ve vzorci chybel, ukazovala
  // vyloha vic, nez pokladna uctovala, a `expectedTotalMinor` byl o tutez
  // castku slepy.
  //
  // VĚRNOSTNÍ BODY (#40) PATŘÍ DO SOUČTU MEZI KUPÓN A POUKAZ – přesně v tom
  // pořadí, v jakém je dělí server (`splitCartDiscounts`). Strop `maxPoints`
  // i kurz `pointValueMinor` jsou ze serveru; jediné, co výloha dělá, je
  // násobení celým kurzem, které nikdy nedá zlomek haléře.
  const maxPoints = loyalty?.maxPoints ?? 0;
  const pointsToRedeem = Math.max(0, Math.min(pointsWanted, maxPoints));
  const pointsDiscount = pointsToRedeem * (loyalty?.pointValueMinor ?? 0);
  // POUKAZ SE DĚLÍ AŽ ZA BODY, takže uplatněné body mu ubírají prostor.
  // `POST /api/shop/voucher` o bodech neví a vrací `min(zůstatek, základ po
  // kupónu)`; zbytek základu po bodech přitom drží náhled bodů
  // (`redeemableBaseMinor`). Minimum z těch dvou je BAJT PO BAJTU totéž, co
  // spočítá `splitCartDiscounts` v pokladně:
  //     min(zůstatek, základ − kupón)  a  min(…, základ − kupón − body)
  //   = min(zůstatek, základ − kupón − body).
  // Kdyby tenhle člen chyběl, ukazoval by souhrn větší čerpání poukazu, než
  // pokladna uzná – součet by vyšel níž, než se účtuje, a objednávka by
  // spadla na stropu `expectedTotalMinor`.
  //
  // BEZ UPLATNĚNÝCH BODŮ SE NEUPLATNÍ ANI TENHLE ČLEN, a je to záměr, ne
  // zkratka: s nulou bodů je minimum matematicky bez účinku (`voucherApplied`
  // je ze stejného základu, takže je vždy menší), ale obě čísla chodí ze DVOU
  // různých dotazů. Kdyby mezi nimi začala akce, mohl by starší z nich strop
  // podstřelit a objednávka by spadla na `expectedVoucherMinor` u zákazníka,
  // který o body vůbec nezavadil. Takhle je chování bez bodů bajt po bajtu
  // dnešní a člen se zapne přesně tam, kde je potřeba.
  const voucherApplied = voucher?.appliedMinor ?? 0;
  const voucherDiscount =
    pointsDiscount === 0 || loyalty === null
      ? voucherApplied
      : Math.min(voucherApplied, Math.max(0, loyalty.redeemableBaseMinor - pointsDiscount));
  // HODNOTU ZBOZI POCITA SERVER, ne vyloha.
  //
  // Klient nezna mnozstevni slevy ani darkove radky (darek vstupuje do
  // souhrnu plnou cenou a hned ji cely odecte sleva), takze si soucet
  // nemuze slozit, aniz by se rozesel se zavaznou castkou z pokladny.
  // Presne tohle byla chyba: `promoDiscountMinor` obsahuje i slevu darku,
  // ale darek v souctu kosiku nikdy nebyl, takze se odecetl navic.
  //
  // Bez nakonfigurovanych akci prijde ze serveru `null` a plati dosavadni
  // soucet z `delivery-options` – chovani je pak bajt po bajtu dnesni.
  const goodsBeforePromo = promo?.itemsTotalMinor ?? itemsTotal;
  const goodsTotal = promo?.goodsTotalMinor ?? itemsTotal;
  // Akcni sleva na KOSIKOVYCH radcich. Sleva darkoveho radku v ni neni –
  // ta je videt primo u darku (prekrtnuta cena + „zdarma").
  const promoDiscount = goodsBeforePromo - goodsTotal;
  const total = Math.max(
    0,
    goodsTotal +
      shippingCost +
      (payment?.feeMinor ?? 0) -
      discount -
      pointsDiscount -
      voucherDiscount,
  );

  /**
   * Znovu nacte cisla, ze kterych se sklada souhrn.
   *
   * Vola se po chybe `expectedTotalMinor`: pokladna prave rekla, ze cena
   * uz je jina, takze zakaznik musi videt AKTUALNI souhrn, ne ten odmitnuty.
   *
   * VRACI, jestli mezitim vyprselo PRIHLASENI (401 z nahledu bodu). Volajici
   * podle toho rozhodne, jestli zakaznikovi rict „zustatek klesl", nebo
   * „prihlas se znovu" – obe situace prijdou ze serveru se stejnym
   * `field: 'pointsToRedeem'` a splest je znamena poslat cloveka opravovat
   * neco jineho, nez je rozbite.
   */
  async function refreshSummary(): Promise<boolean> {
    let signedOut = false;
    // VERNOSTNI BODY: zustatek i strop se musi nacist znovu. Strop zavisi na
    // akcich a kuponu, ktere se prave zmenily, a zustatek mohla mezitim
    // spotrebovat jina objednavka. Efekt vys se na to nespousti (kosik ani
    // kupon se nezmenily), takze se o to starame tady - stejne jako u poukazu.
    if (customer !== undefined && customer !== null) {
      const refreshed = await api.loyaltyPreview(items, coupon?.code).catch((caught: unknown) => {
        if (caught instanceof ShopError && caught.status === 401) signedOut = true;
        return null;
      });
      if (refreshed === null) {
        setLoyalty(null);
        setPointsWanted(0);
      } else {
        setLoyalty(refreshed.loyalty);
        setPointsSignedOut(false);
        setPointsWanted((wanted) => Math.min(wanted, refreshed.loyalty.maxPoints));
      }
    }
    if (signedOut) setPointsSignedOut(true);
    try {
      const [delivery, promotions] = await Promise.all([
        api.deliveryOptions(items, shippingMethod === '' ? undefined : shippingMethod),
        // Nahled akci je fail-silent uz v efektu vys – stejne i tady.
        api.fetchPromotions(items).catch(() => null),
      ]);
      setOptions(delivery);
      setPromo(promotions);
      // Poukaz taky - jeho uplatnena castka zavisi na akcich, ktere se
      // prave zmenily. Efekt vys se na tuhle zmenu nespousti (kod poukazu
      // je porad tentyz), takze se o nej musime postarat tady.
      if (voucher !== null) {
        const refreshed = await api
          .applyVoucher(items, voucher.code, coupon?.code)
          .catch(() => null);
        setVoucher(refreshed === null ? null : refreshed.voucher);
      }
    } catch {
      // Kdyz se nova cisla nepodari nacist, zustane souhrn, jaky byl.
      // Hlaska od serveru je videt tak jako tak.
    }
    return signedOut;
  }

  async function applyCoupon() {
    const code = couponCode.trim();
    if (code === '' || couponBusy) return;

    setCouponBusy(true);
    setCouponError(null);
    try {
      const result = await api.applyCoupon(items, code, email.trim());
      setCoupon(result.coupon);
    } catch (caught) {
      setCoupon(null);
      setCouponError(caught instanceof ShopError ? caught.message : t('co.errCoupon'));
    } finally {
      setCouponBusy(false);
    }
  }

  async function applyVoucher() {
    const code = voucherCode.trim();
    if (code === '' || voucherBusy) return;

    setVoucherBusy(true);
    setVoucherError(null);
    try {
      // Kupon se uplatnuje PRED poukazem - server ho musi znat, jinak vrati
      // vetsi uplatnenou castku, nez pokladna uzna.
      const result = await api.applyVoucher(items, code, coupon?.code);
      // Klic si poznamenavame JESTE PRED `setVoucher`: to prekresli stranku,
      // rozjede efekt vys a ten by jinak poslal tentyz dotaz podruhe.
      //
      // Klic se sklada z kodu, jak ho VRATIL SERVER (`result.voucher.code`),
      // ne z toho, co clovek napsal do pole: efekt vys pracuje s `voucher.code`,
      // takze pri zadani malymi pismeny by se klice lisily a dotaz by odesel
      // podruhe presne tam, kde ho zavirame.
      lastVoucherQuery.current = voucherQueryKey(result.voucher.code, coupon?.code ?? null);
      setVoucher(result.voucher);
    } catch (caught) {
      setVoucher(null);
      // Viz efekt vys: hlaska o KUPONU patri ke kuponu, ne k poukazu.
      if (caught instanceof ShopError && caught.field === 'couponCode') {
        setCoupon(null);
        setCouponError(caught.message);
      } else {
        setVoucherError(caught instanceof ShopError ? caught.message : t('co.errVoucher'));
      }
    } finally {
      setVoucherBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;

    setError(null);
    setErrorField(null);
    setSubmitting(true);
    try {
      // Visitor/session klice pro serverovy order_completed event. Bez
      // souhlasu s analytikou je undefined a objednavka odejde bez vazby.
      const tracking = getTrackingContext();
      const { order } = await api.checkout({
        email: email.trim(),
        phone: phone.trim() === '' ? null : phone.trim(),
        items,
        shippingMethod,
        paymentMethod,
        ...(shipping?.requiresPickupPoint !== true
          ? {}
          : shipping.pickupSource === 'ppl_widget'
            ? { pickupPoint: widgetPoint }
            : pickupPoint === null
              ? {}
              : { pickupPointId: pickupPoint.code }),
        billingAddress: {
          fullName: fullName.trim(),
          street: street.trim(),
          city: city.trim(),
          postalCode: postalCode.trim(),
          countryCode,
        },
        note: note.trim(),
        ...(coupon === null ? {} : { couponCode: coupon.code }),
        // Jen poukaz, ktery uz je v souhrnu - jinak by pokladna strhla
        // slevu, kterou vyloha nezapocitala do `expectedTotalMinor`.
        ...(voucher === null ? {} : { voucherCode: voucher.code }),
        termsAccepted: terms,
        // STROP NA UCTOVANOU CASTKU: presne to cislo, ktere je o kus niz
        // vykreslene v radku „Celkem". Kdyz mezitim skoncila akce a pokladna
        // by uctovala vic, objednavka nevznikne a zakaznik dostane hlasku.
        expectedTotalMinor: total,
        // STROP NA CERPANI POUKAZU: presne to cislo, ktere je o kus niz
        // v radku „Dárkový poukaz" – a NULA, kdyz se radek nevykreslil
        // (podminka `voucherDiscount > 0` u radku souhrnu). Posila se VZDY,
        // i s nulou: „nula" je plnohodnotny udaj o tom, co zakaznik videl.
        // Bez nej by pokladna smela strhnout kredit, o kterem souhrn mlcel –
        // hotovost by pritom na halir sedela, takze `expectedTotalMinor` by
        // nemel co zachytit.
        expectedVoucherMinor: voucherDiscount,
        // VĚRNOSTNÍ BODY: počet bodů odchází jen tehdy, když se nějaké
        // opravdu uplatňují – hostovský požadavek s body pokladna odmítne
        // a nula by tam neměla co dělat.
        ...(pointsToRedeem > 0 ? { pointsToRedeem } : {}),
        // STROP NA UPLATNĚNÍ BODŮ: přesně to číslo, které je o kus níž
        // v řádku „Věrnostní body" – a NULA, když se řádek nevykreslil
        // (podmínka `pointsDiscount > 0` u řádku souhrnu). Posílá se VŽDY,
        // i s nulou: bez něj by pokladna směla strhnout body, o kterých
        // souhrn mlčel, a hotovost by přitom seděla na haléř, takže
        // `expectedTotalMinor` by neměl co zachytit.
        expectedPointsMinor: pointsDiscount,
        // Prezentacni mena; kurz i castky resi server. CZK = beze zmeny.
        currency,
        ...(tracking === undefined ? {} : { tracking }),
      });

      // Fronta trackeru se musi vyprazdnit TED – nasleduje redirect na
      // platebni branu (cizi domena) a keepalive fetch prezije unload.
      // order_completed klient neposila, ten emituje server.
      flush({ keepalive: true });

      // Kosik mazeme az po potvrzeni serverem – kdyby objednavka selhala,
      // zakaznik by prisel o obsah kosiku.
      clear();

      // Platba kartou pokracuje na brane. Kdyz se ji nepodari zalozit,
      // objednavka uz existuje – zakaznik skonci na dekovacce, kde ma
      // tlacitko „zkusit znovu". Rozhodne ho nesmime nechat na checkoutu
      // s dojmem, ze se objednavka neodeslala.
      if (order.paymentMethod === 'comgate') {
        // Po navratu z brany je stav Reactu pryc – prohlizec sel na cizi
        // doménu a zpatky. E-mail si proto odlozime, aby se dekovacka
        // mohla zeptat na skutecny stav platby.
        rememberOrder(order.orderNumber, order.email);
        try {
          const { redirectUrl } = await api.startPayment(order.orderNumber, order.email);
          if (redirectUrl !== null) {
            window.location.href = redirectUrl;
            return;
          }
        } catch {
          // Na branu se nedostaneme; pokracujeme na dekovacku.
        }
      }

      void navigate(`/hotovo/${order.orderNumber}`, {
        replace: true,
        state: {
          total: order.totalMinor,
          email: order.email,
          paymentMethod: order.paymentMethod,
          currency: order.currency,
          exchangeRate: order.exchangeRate,
          // Akce z odpovedi checkoutu (server) – dekovacka je jen vypise.
          promoDiscountMinor: order.promoDiscountMinor ?? 0,
          promotions: order.promotions ?? [],
        },
      });
    } catch (caught) {
      if (caught instanceof ShopError) {
        // VĚRNOSTNÍ BODY: obě chyby mají společné to, že se mezi náhledem
        // a pokladnou změnilo něco, co zákazník na souhrnu viděl — a že
        // objednávka NEVZNIKLA CELÁ (kontroly sedí před prvním zápisem, takže
        // po sobě nenechají ani nárok akce, ani rezervaci skladu, ani odečet
        // bodů). Souhrn proto načteme znovu VČETNĚ zůstatku a řekneme
        // přeloženou větou, co se stalo. Serverová hláška je jen česky
        // a „prázdná obrazovka" tu není odpověď.
        //
        //   * `expectedPointsMinor` — cena se změnila (skončila akce), takže
        //     by se strhlo víc bodů, než souhrn ukázal;
        //   * `pointsToRedeem` — zůstatek klesl (jiná objednávka, storno),
        //     nebo vypršelo přihlášení. Rozliší je 401 z nového náhledu:
        //     poslat člověka „doplnit body", když se má přihlásit, je horší
        //     než mu neříct nic.
        if (caught.field === 'pointsToRedeem' || caught.field === 'expectedPointsMinor') {
          const signedOut = await refreshSummary();
          setError(
            caught.field === 'expectedPointsMinor'
              ? t('co.errPointsChanged')
              : signedOut
                ? t('co.errPointsGuest')
                : t('co.errPointsBalance'),
          );
          setErrorField(caught.field);
          return;
        }
        // U cerpani poukazu rikame presneji, co se stalo: penezni castka
        // sedi, zmenilo se cerpani poukazu. Hlasku bereme z prekladu
        // (locales.ts), stejne jako u kuponu; serverova veta je cesky.
        setError(
          caught.field === 'expectedVoucherMinor' ? t('co.errVoucherChanged') : caught.message,
        );
        setErrorField(caught.field);
        // Cena nebo cerpani poukazu se mezi zobrazenim a odeslanim zmenilo.
        // Necha-li se v souhrnu uz neplatne cislo, zakaznik jen znovu klikne
        // a znovu narazi.
        if (caught.field === 'expectedTotalMinor' || caught.field === 'expectedVoucherMinor') {
          await refreshSummary();
        }
      } else {
        setError(t('co.submitError'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <h1>{t('co.title')}</h1>

      {error !== null && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="checkout">
        <div className="checkout-main">
          <section className="card">
            <h2>{t('co.contact')}</h2>
            <div className="form-grid">
              <label className="field">
                <span>{t('co.email')}</span>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span>{t('co.phone')}</span>
                <input
                  name="phone"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </label>
            </div>
          </section>

          <section className="card">
            <h2>{t('co.shippingAddress')}</h2>
            <div className="form-grid">
              <label className="field span-full">
                <span>{t('co.fullName')}</span>
                <input
                  name="fullName"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  required
                />
              </label>
              <label className="field span-full">
                <span>{t('co.street')}</span>
                <input
                  name="street"
                  value={street}
                  onChange={(event) => setStreet(event.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span>{t('co.city')}</span>
                <input
                  name="city"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span>{t('co.zip')}</span>
                <input
                  name="postalCode"
                  value={postalCode}
                  onChange={(event) => setPostalCode(event.target.value)}
                  required
                />
              </label>
              <label className="field span-full">
                <span>{t('co.country')}</span>
                <select
                  name="countryCode"
                  value={countryCode}
                  onChange={(event) => setCountryCode(event.target.value)}
                >
                  <option value="CZ">{t('co.countryCZ')}</option>
                  <option value="SK">{t('co.countrySK')}</option>
                  <option value="DE">{t('co.countryDE')}</option>
                  <option value="PL">{t('co.countryPL')}</option>
                  <option value="AT">{t('co.countryAT')}</option>
                </select>
              </label>
              {countryCode !== 'CZ' && (
                <p className="muted small span-full">
                  {t('co.ossNote')}
                </p>
              )}
            </div>
          </section>

          <section className={errorField === 'shippingMethod' ? 'card has-error' : 'card'}>
            <h2>{t('co.shipping')}</h2>
            {options === null ? (
              <p className="muted">{t('co.loading')}</p>
            ) : (
              <div className="options">
                {options.shipping.map((offer) => (
                  <label
                    key={offer.code}
                    className={shippingMethod === offer.code ? 'option selected' : 'option'}
                  >
                    <input
                      type="radio"
                      name="shipping"
                      value={offer.code}
                      checked={shippingMethod === offer.code}
                      onChange={() => {
                        setShippingMethod(offer.code);
                        // Misto patri konkretnimu dopravci – pri zmene ho
                        // musime zahodit, jinak by odeslo cizi.
                        setPickupPoint(null);
                        setWidgetPoint(null);
                      }}
                    />
                    <span className="option-body">
                      <strong>{offer.name}</strong>
                      <span className="muted small">{offer.description}</span>
                      {offer.missingToFreeMinor !== null && (
                        <span className="muted small">
                          {t('co.freeOver', { amount: format(offer.missingToFreeMinor + itemsTotal) })}
                        </span>
                      )}
                    </span>
                    <span className="option-price">
                      {offer.isFree ? (
                        <>
                          <s className="muted">{format(offer.basePriceMinor)}</s> {t('co.free')}
                        </>
                      ) : (
                        format(offer.priceMinor)
                      )}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {shipping?.requiresPickupPoint === true && (
              <div className={errorField === 'pickupPointId' ? 'pickup has-error' : 'pickup'}>
                <h3>{t('co.pickPoint')}</h3>
                {pickupPoint !== null && shipping.pickupSource !== 'ppl_widget' && (
                  <p className="picked" role="status">
                    <strong>{pickupPoint.name}</strong>
                    <span className="muted small">
                      {pickupPoint.street}, {pickupPoint.postalCode} {pickupPoint.city}
                    </span>
                  </p>
                )}
                {shipping.pickupSource === 'ppl_widget' ? (
                  <PplWidgetPicker
                    apiKey={shipping.widgetApiKey}
                    selected={widgetPoint}
                    onSelect={setWidgetPoint}
                    requireCashOnDelivery={payment?.isCashOnDelivery === true}
                  />
                ) : (
                  <PickupPointPicker
                    carrier={shipping.code}
                    selected={pickupPoint}
                    onSelect={setPickupPoint}
                  />
                )}
              </div>
            )}
          </section>

          <section className={errorField === 'paymentMethod' ? 'card has-error' : 'card'}>
            <h2>{t('co.payment')}</h2>
            {options === null ? (
              <p className="muted">{t('co.loading')}</p>
            ) : (
              <div className="options">
                {options.payment.map((option) => (
                  <label
                    key={option.code}
                    className={paymentMethod === option.code ? 'option selected' : 'option'}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value={option.code}
                      checked={paymentMethod === option.code}
                      onChange={() => setPaymentMethod(option.code)}
                    />
                    <span className="option-body">
                      <strong>{option.name}</strong>
                      <span className="muted small">{option.description}</span>
                    </span>
                    <span className="option-price">
                      {option.feeMinor === 0 ? t('co.free') : `+ ${format(option.feeMinor)}`}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </section>

          <section className="card">
            <h2>{t('co.note')}</h2>
            <label className="field">
              <span>{t('co.noteHint')}</span>
              <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
            </label>
          </section>
        </div>

        <aside className="checkout-summary">
          <section className="card">
            <h2>{t('co.summary')}</h2>
            <dl className="summary">
              <dt>{t('co.goods')}</dt>
              <dd>{format(goodsBeforePromo)}</dd>
              <dt>{t('co.shipping')}</dt>
              <dd>
                {shipping === null
                  ? '—'
                  : coupon?.freeShipping === true
                    ? t('co.free')
                    : format(shipping.priceMinor)}
              </dd>
              {payment !== null && payment.feeMinor > 0 && (
                <>
                  <dt>{payment.name}</dt>
                  <dd>{format(payment.feeMinor)}</dd>
                </>
              )}
              {/* Darky – radek za plnou cenu proti nemu sleva „zdarma". */}
              {promo?.gifts.map((gift) => (
                <Fragment key={`gift-${gift.variantId}`}>
                  <dt className="gift-line">
                    <span className="tag promo">{t('promo.gift')}</span> {gift.productName}
                  </dt>
                  <dd>
                    <s className="muted">{format(gift.unitPriceMinor * gift.quantity)}</s>{' '}
                    <span className="gift-free">{t('promo.giftFree')}</span>
                  </dd>
                </Fragment>
              ))}
              {/* „Akce" NAD kuponovou slevou – poradi na obrazovce odpovida
                  poradi vypoctu (akce, pak kupon z akcniho zakladu). */}
              {promoDiscount > 0 && (
                <>
                  <dt>{t('co.promoDiscount')}</dt>
                  <dd className="discount">− {format(promoDiscount)}</dd>
                </>
              )}
              {discount > 0 && (
                <>
                  <dt>{t('co.discount', { code: coupon?.code ?? '' })}</dt>
                  <dd className="discount">− {format(discount)}</dd>
                </>
              )}
              {/* VĚRNOSTNÍ BODY mezi kupónem a poukazem – pořadí na obrazovce
                  odpovídá pořadí výpočtu v pokladně (akce, kupón, BODY,
                  poukaz). Řádek se kreslí stejně jako kupón a poukaz a právě
                  tahle částka odchází jako `expectedPointsMinor`. */}
              {pointsDiscount > 0 && (
                <>
                  <dt>{t('co.points')}</dt>
                  <dd className="discount">− {format(pointsDiscount)}</dd>
                </>
              )}
              {/* Poukaz az POD kuponem - poradi na obrazovce odpovida
                  poradi vypoctu (akce, kupon, pak poukaz ze zbytku). */}
              {voucherDiscount > 0 && (
                <>
                  <dt>{t('co.voucher')}</dt>
                  <dd className="discount">− {format(voucherDiscount)}</dd>
                </>
              )}
              <dt className="total">{t('co.total')}</dt>
              <dd className="total">{format(total)}</dd>
            </dl>

            {isConverted && (
              <p className="muted small settlement-note">
                {t('co.settlement', { amount: formatCzk(total) })}
              </p>
            )}

            <div className="coupon">
              {coupon === null ? (
                <>
                  <label className="field">
                    <span>{t('co.couponLabel')}</span>
                    <div className="coupon-row">
                      <input
                        name="couponCode"
                        value={couponCode}
                        onChange={(event) => setCouponCode(event.target.value)}
                        placeholder="např. LETO10"
                        onKeyDown={(event) => {
                          // Enter v poli kuponu nesmi odeslat celou objednavku.
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void applyCoupon();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="button ghost"
                        onClick={() => void applyCoupon()}
                        disabled={couponBusy || couponCode.trim() === ''}
                      >
                        {t('co.couponApply')}
                      </button>
                    </div>
                  </label>
                  {couponError !== null && <p className="field-error">{couponError}</p>}
                </>
              ) : (
                <p className="coupon-applied">
                  {t('co.couponApplied', { code: coupon.code })}{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setCoupon(null);
                      setCouponCode('');
                    }}
                  >
                    {t('cart.remove')}
                  </button>
                </p>
              )}

              {voucher === null ? (
                <>
                  <label className="field voucher-field">
                    <span>{t('co.voucher')}</span>
                    <div className="coupon-row">
                      <input
                        name="voucherCode"
                        value={voucherCode}
                        onChange={(event) => setVoucherCode(event.target.value)}
                        placeholder="např. DAREK-XXXX-XXXX"
                        autoComplete="off"
                        onKeyDown={(event) => {
                          // Enter v poli poukazu nesmi odeslat celou objednavku.
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void applyVoucher();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="button ghost"
                        onClick={() => void applyVoucher()}
                        disabled={voucherBusy || voucherCode.trim() === ''}
                      >
                        {t('co.voucherApply')}
                      </button>
                    </div>
                  </label>
                  {voucherError !== null && <p className="field-error">{voucherError}</p>}
                </>
              ) : (
                <p className="coupon-applied">
                  {t('co.voucherApplied', { code: voucher.code })}{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setVoucher(null);
                      setVoucherCode('');
                      setVoucherError(null);
                    }}
                  >
                    {t('cart.remove')}
                  </button>
                </p>
              )}

              {/* VĚRNOSTNÍ BODY (#40).
                  Host tu NEDOSTANE políčko, které by skončilo chybou – uplatnit
                  body smí jen přihlášený zákazník (server odmítne i požadavek
                  s body bez session), takže mu rovnou řekneme proč a kudy. */}
              {customer === undefined ? null : customer === null || pointsSignedOut ? (
                <p className="muted small points-note">
                  {t('co.pointsGuest')}{' '}
                  <Link className="link" to="/registrace">
                    {t('co.pointsGuestLink')}
                  </Link>
                </p>
              ) : loyalty === null ? null : loyalty.balancePoints <= 0 ? (
                <p className="muted small points-note">{t('co.pointsNone')}</p>
              ) : maxPoints === 0 ? (
                // Body na účtu jsou, ale do košíku se žádný nevejde – kupón
                // nebo akce spotřebovaly celou hodnotu zboží. Nabídnout
                // políčko by znamenalo slíbit slevu, kterou pokladna neuzná.
                <p className="muted small points-note">{t('co.pointsNoFit')}</p>
              ) : (
                <div
                  className={
                    errorField === 'pointsToRedeem' || errorField === 'expectedPointsMinor'
                      ? 'field points-field has-error'
                      : 'field points-field'
                  }
                >
                  <label className="terms">
                    <input
                      type="checkbox"
                      name="usePoints"
                      checked={pointsToRedeem > 0}
                      onChange={(event) =>
                        setPointsWanted(event.target.checked ? maxPoints : 0)
                      }
                    />
                    <span>{t('co.pointsUse')}</span>
                  </label>
                  <p className="muted small">
                    {t('co.pointsBalance', {
                      points: loyalty.balancePoints,
                      amount: format(loyalty.balancePoints * loyalty.pointValueMinor),
                    })}
                  </p>
                  {pointsToRedeem > 0 && (
                    <>
                      <div className="coupon-row">
                        <input
                          type="number"
                          name="pointsToRedeem"
                          min={0}
                          max={maxPoints}
                          step={1}
                          value={pointsToRedeem}
                          aria-label={t('co.pointsCount')}
                          onChange={(event) =>
                            setPointsWanted(clampPoints(event.target.value, maxPoints))
                          }
                          onKeyDown={(event) => {
                            // Enter v policku bodu nesmi odeslat celou objednavku.
                            if (event.key === 'Enter') event.preventDefault();
                          }}
                        />
                        <button
                          type="button"
                          className="button ghost"
                          onClick={() => setPointsWanted(maxPoints)}
                          disabled={pointsToRedeem === maxPoints}
                        >
                          {t('co.pointsAll')}
                        </button>
                      </div>
                      <p className="muted small">
                        {t('co.pointsMax', {
                          points: maxPoints,
                          amount: format(maxPoints * loyalty.pointValueMinor),
                        })}
                      </p>
                      {/* Uplatněné body jsou ZPŮSOB PLATBY téhle objednávky.
                          Při částečné vratce se nevracejí (vědomé rozhodnutí,
                          `orders/credit-release.ts`) – tak to zákazníkovi
                          řekneme dřív, než body utratí. */}
                      <p className="muted small">{t('co.pointsRefundNote')}</p>
                    </>
                  )}
                </div>
              )}
            </div>

            <label className="terms">
              <input
                type="checkbox"
                name="terms"
                checked={terms}
                onChange={(event) => setTerms(event.target.checked)}
              />
              <span>{t('co.terms')}</span>
            </label>

            <button
              type="submit"
              className="button primary wide"
              disabled={
                submitting ||
                shippingMethod === '' ||
                paymentMethod === '' ||
                (shipping?.requiresPickupPoint === true &&
                  (shipping.pickupSource === 'ppl_widget'
                    ? widgetPoint === null
                    : pickupPoint === null))
              }
            >
              {submitting ? t('co.submitting') : t('co.submit')}
            </button>
          </section>
        </aside>
      </div>
    </form>
  );
}
